import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  PhotoIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { GripVertical, FileText, ListOrdered, MessageSquareText, Lightbulb, Video } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import { Button } from '@/components/ui/button'
import DeleteBlockDialog from './DeleteBlockDialog'

import OverflowMenu from '@components/OverflowMenu'
import { stripMarkdown } from '@helpers/format'

const formatPreviewLabel = (text, fallback) => {
  if (!text) return fallback

  return stripMarkdown(text)
}

const BlockPreview = ({ id, blockId, __typename, index, onDelete, selected, setSelectedBlockId, setBlockIdToDelete, refetch, onDeleted, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: blockId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='flex flex-col group/block w-full'
    >
      <div
        className={twMerge(
          selected && 'bg-accent font-semibold',
          'w-full flex hover:bg-accent/80 rounded-md py-2 border border-border px-1 gap-1 items-center leading-snug'
        )}
        style={{ cursor: 'pointer' }}
      >
        <GripVertical {...attributes} {...listeners} className="text-gray-400 hover:text-gray-600 shrink-0 cursor-grab" />
        <div className='flex flex-row justify-between items-center gap-1 w-full'
          onClick={() => setSelectedBlockId(blockId)}>
          <Choose>
            <When condition={__typename === 'Text'}>
              <FileText className='size-7 bg-purple-200 text-purple-900 rounded-md p-1 shrink-0 hover:cursor-grab' />
              <div className='flex-1 min-w-0 ml-1'>
                <p className='line-clamp-2 break-all'>{formatPreviewLabel(props.content, 'Content')}</p>
              </div>
            </When>

            <When condition={__typename === 'Reflection'}>
              <Lightbulb className='size-7 bg-red-200 text-red-900 rounded-md p-1 shrink-0 hover:cursor-grab' />
              <div className='flex-1 min-w-0 ml-1'>
                <p className='line-clamp-2 break-all'>{formatPreviewLabel(props.question, 'Reflection')}</p>
              </div>
            </When>

            <When condition={__typename === 'Question'}>
              <ListOrdered className='size-7 bg-yellow-200 text-yellow-900 rounded-md p-1 shrink-0 hover:cursor-grab' />
              <div className='flex-1 min-w-0 ml-1'>
                <p className='line-clamp-2 break-all'>{formatPreviewLabel(props.label, 'Multiple Choice')}</p>
              </div>
            </When>

            <When condition={__typename === 'FreeResponse'}>
              <MessageSquareText className='size-7 bg-orange-200 text-orange-900 rounded-md p-1 shrink-0 hover:cursor-grab' />
              <div className='flex-1 min-w-0 ml-1'>
                <p className='line-clamp-2 break-all'>{formatPreviewLabel(props.question, 'Free Response')}</p>
              </div>
            </When>

            <When condition={__typename === 'Video'}>
              <Video className='size-7 bg-purple-200 text-purple-900 rounded-md p-1 shrink-0 hover:cursor-grab' />
              <div className='flex-1 min-w-0 ml-1'>
                <p className='line-clamp-2 break-all'>Video</p>
              </div>
            </When>

            <When condition={__typename === 'Image'}>
              <PhotoIcon className='size-7 bg-purple-200 rounded-md p-1 shrink-0 hover:cursor-grab' />
              <div className='flex-1 min-w-0 ml-1'>
                <p className='line-clamp-2 break-all'>Image</p>
              </div>
            </When>
          </Choose>

          {/* Wrapper div to stop click propagation from the delete dialog area */}
          <DeleteBlockDialog
            blockId={blockId}
            refetch={refetch}
            onDeleted={onDeleted}
            trigger={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive hover:bg-transparent"
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            }
          />
        </div>
      </div>
    </div>
  )
}

export default BlockPreview
